/**
 * Speech writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const speechWritingFormatsPack: ToilePack = {
    slug: 'speech-writing-formats',
    category: 'classroom',
    name: 'Speech writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'assembly', label: 'Assembly speech', subject: 'a speech format with "Greeting", "Introduction", "Body", "Conclusion", "Thank you" labelled'},
        {key: 'farewell', label: 'Farewell speech', subject: 'a speech format on a podium-shaped page with the parts boxed'},
        {key: 'national', label: 'National day', subject: 'a speech format with flag doodles and labelled parts'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Speech Writing Format: Assembly, Farewell, National Day',
        metaDescription: 'Speech writing format on one labelled page: an assembly speech, a farewell speech and a national day speech, in three styles.',
        h1: 'Speech writing format: assembly, farewell and national day speech',
        tagline: 'Nine labelled pages, three speeches in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing the layout of a speech, with each part boxed and named: greeting, introduction, body, conclusion and thank you. The nine pages show an assembly speech, a farewell speech and a national day speech with flag doodles. Ask for another occasion and the same labelled layout is asked for.',
        useCases: [
            'a classroom wall chart',
            'a reference for a speaking lesson',
            'a planning sheet before an assembly',
            'a handout for a school function',
        ],
        faq: [
            {q: 'What are the parts of a speech?', a: 'The greeting, introduction, body, conclusion and thank you, each boxed on the page.'},
            {q: 'Does the page include speech text?', a: 'No. The body is drawn as ruled lines, so the page shows the layout, not the words.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for a speech for another occasion?', a: 'Yes. Add a direction for another occasion and the labelled layout is asked for with it.'},
        ],
    },
}
